import { useState, useEffect } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';

export interface Announcement {
  id: string;
  title: string;
  message: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  gradient_color?: string;
}

/**
 * Hook to fetch active announcements from database
 * Returns active announcements ordered by display_order
 */
export function useAnnouncements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch active announcements that haven't expired
        const { data, error: fetchError } = await supabase
          .from('announcements')
          .select('*')
          .eq('is_active', true)
          .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString())
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (fetchError) {
          logger.warn('Error fetching announcements:', fetchError);
          setAnnouncements([]);
          return;
        }

        if (data) {
          setAnnouncements(data as Announcement[]);
        } else {
          setAnnouncements([]);
        }
      } catch (err) {
        logger.error('Error in useAnnouncements:', err);
        setError(err instanceof Error ? err.message : 'Failed to load announcements');
        setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();

    // Refresh every 60 seconds to catch updates
    const interval = setInterval(fetchAnnouncements, 60000);
    return () => clearInterval(interval);
  }, []);

  return { announcements, loading, error };
}

